/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { RefObject } from 'preact';
import type { Dispatch, MutableRef } from 'preact/hooks';
import type {
  TableHorizontalScrollPosition,
  TableVerticalScrollPosition,
  TableViewportConfig
} from '#UNSAFE_TableView';
import type {
  AppliedColumnWidths,
  OverflowInfo,
  ScrollDetail,
  SizingInfo,
  StickyColumnInfo,
  TableColumn,
  ViewportInfo
} from '../Table';

import { findElementByKey } from '#utils/PRIVATE_collectionUtils';
import { getRowKey } from '#PRIVATE_TableCommon/utils/DomUtils';
import {
  getHorizontalOverflowDiff,
  getScrollbarHeight,
  getScrollbarWidth,
  getVerticalOverflowDiff,
  isHTMLScroller,
  isLeftHTMLScrollbar
} from '#PRIVATE_TableCommon/utils/ScrollUtils';
import { scrollerStyles } from '../themes/Table.css';
import {
  getColumnKey,
  getElementFromCell,
  getFooterRowElement,
  getHeaderRowElement,
  getRowKeyForRowIndex,
  getSpacerRowElement,
  TABLE_DATA_SELECTOR,
  TABLE_FOOTER_SELECTOR,
  TABLE_HEADER_SELECTOR,
  TABLE_ROW_SELECTOR
} from './TableDomUtils';

/**
 * Helper method to return the current scrollbar height of the scroller element.
 */
const _getScrollbarHeight = (scrollerElement: HTMLElement, sizingInfo: SizingInfo) => {
  return sizingInfo.isInitialRender
    ? getScrollbarHeight(scrollerElement)
    : sizingInfo.boxHeight - sizingInfo.contentHeight;
};

/**
 * Helper method to return the current scrollbar width of the scroller element.
 */
const _getScrollbarWidth = (
  scrollerElement: HTMLElement,
  viewportInfo: ViewportInfo,
  sizingInfo: SizingInfo,
  overflowInfo: OverflowInfo
) => {
  if (viewportInfo.isExternalScroller || overflowInfo.isVertical) {
    return sizingInfo.isInitialRender
      ? getScrollbarWidth(scrollerElement)
      : sizingInfo.boxWidth - sizingInfo.contentWidth;
  }
  return 0;
};

/**
 * Helper method to get the top scroller offset of the Table
 */
const _getScrollerTopOffset = (
  rootElement: HTMLDivElement,
  viewportConfig: TableViewportConfig,
  tableId: string
) => {
  const headerRowElement = getHeaderRowElement(rootElement, tableId);
  const top = viewportConfig.scrollerOffsetTop ?? 0;
  return top + (headerRowElement != null ? headerRowElement.getBoundingClientRect().height : 0);
};

/**
 * Helper method to get the bottom scroller offset of the Table
 */
const _getScrollerBottomOffset = (
  rootElement: HTMLDivElement,
  viewportConfig: TableViewportConfig,
  tableId: string
) => {
  const footerRowElement = getFooterRowElement(rootElement, tableId);
  const bottom = viewportConfig.scrollerOffsetBottom ?? 0;
  return bottom + (footerRowElement != null ? footerRowElement.getBoundingClientRect().height : 0);
};

/**
 * Helper method to get the start scroller offset of the Table
 */
const _getScrollerStartOffset = (viewportConfig: TableViewportConfig) => {
  return viewportConfig.scrollerOffsetStart ?? 0;
};

/**
 * Helper method to get the end scroller offset of the Table
 */
const _getScrollerEndOffset = (viewportConfig: TableViewportConfig) => {
  return viewportConfig.scrollerOffsetEnd ?? 0;
};

/**
 * Helper method to return the top position of the scroller element.
 */
export const getScrollerTop = (
  rootElement: HTMLDivElement,
  scrollerElement: HTMLElement,
  viewportConfig: TableViewportConfig,
  tableId: string
) => {
  const scrollerTopOffset = _getScrollerTopOffset(rootElement, viewportConfig, tableId);
  return isHTMLScroller(scrollerElement)
    ? scrollerTopOffset
    : scrollerTopOffset + scrollerElement.getBoundingClientRect().top;
};

/**
 * Helper method to return the bottom position of the scroller element.
 */
export const getScrollerBottom = (
  rootElement: HTMLDivElement,
  scrollerElement: HTMLElement,
  viewportConfig: TableViewportConfig,
  sizingInfo: SizingInfo,
  tableId: string
) => {
  const bottomOffset = _getScrollerBottomOffset(rootElement, viewportConfig, tableId);
  if (isHTMLScroller(scrollerElement)) {
    return scrollerElement.clientHeight - bottomOffset;
  }
  const scrollbarHeight = _getScrollbarHeight(scrollerElement, sizingInfo);
  return scrollerElement.getBoundingClientRect().bottom - bottomOffset - scrollbarHeight;
};

/**
 * Helper method to return the start position of the scroller element.
 */
export const getScrollerStart = (
  scrollerElement: HTMLElement,
  viewportInfo: ViewportInfo,
  sizingInfo: SizingInfo,
  overflowInfo: OverflowInfo,
  stickyOffset: number,
  isRtl: boolean
) => {
  const startOffset = stickyOffset + _getScrollerStartOffset(viewportInfo.viewportConfig);
  if (isRtl) {
    if (isHTMLScroller(scrollerElement)) {
      const scrollbarOffset = isLeftHTMLScrollbar(scrollerElement)
        ? _getScrollbarWidth(scrollerElement, viewportInfo, sizingInfo, overflowInfo)
        : 0;
      return scrollerElement.clientWidth - startOffset + scrollbarOffset;
    }
    return scrollerElement.getBoundingClientRect().right - startOffset;
  }
  return isHTMLScroller(scrollerElement)
    ? startOffset
    : scrollerElement.getBoundingClientRect().left + startOffset;
};

/**
 * Helper method to return the end position of the scroller element.
 */
export const getScrollerEnd = (
  scrollerElement: HTMLElement,
  viewportInfo: ViewportInfo,
  sizingInfo: SizingInfo,
  overflowInfo: OverflowInfo,
  stickyOffset: number,
  isRtl: boolean
) => {
  const endOffset = stickyOffset + _getScrollerEndOffset(viewportInfo.viewportConfig);
  if (isHTMLScroller(scrollerElement)) {
    if (isRtl) {
      const scrollbarOffset = isLeftHTMLScrollbar(scrollerElement)
        ? _getScrollbarWidth(scrollerElement, viewportInfo, sizingInfo, overflowInfo)
        : 0;
      return endOffset + scrollbarOffset;
    }
    return scrollerElement.clientWidth - endOffset;
  }
  const scrollbarOffset = _getScrollbarWidth(
    scrollerElement,
    viewportInfo,
    sizingInfo,
    overflowInfo
  );
  return isRtl
    ? scrollerElement.getBoundingClientRect().left + endOffset + scrollbarOffset
    : scrollerElement.getBoundingClientRect().right - endOffset - scrollbarOffset;
};

/**
 * A helper function to make sure specified elem is visible in the specified container
 */
function _scrollColumnIntoView<K, D, C extends string>(
  columnElement: HTMLElement,
  scrollerElement: HTMLElement,
  viewportInfo: ViewportInfo,
  columnsArray: TableColumn<K, D, C>[],
  stickyColumnInfo: StickyColumnInfo,
  sizingInfo: SizingInfo,
  overflowInfo: OverflowInfo,
  isRtl: boolean,
  appliedColumnWidths?: AppliedColumnWidths<C>,
  location?: 'inView' | 'start' | 'end'
) {
  const columnKey = getColumnKey(columnElement);

  let columnIndex;
  for (let i = 0; i < columnsArray.length; i++) {
    if (columnsArray[i].key === columnKey) {
      columnIndex = i;
      break;
    }
  }
  if (columnIndex != null && appliedColumnWidths != null) {
    let startOffset = 0;
    let endOffset = 0;

    const borderOffset = appliedColumnWidths.borderOffset;
    const stickyColumns = stickyColumnInfo.stickyColumns;
    for (let i = 0; i < stickyColumns.length; i++) {
      const stickyIndex = stickyColumns[i];
      if (stickyIndex < columnIndex) {
        startOffset += appliedColumnWidths.realColumnWidths[stickyIndex];
      } else {
        break;
      }
    }
    // include the logical 'start' border of this column if owned by the previous column
    // if this column is sticky - otherwise that border should already be visible
    if (
      !appliedColumnWidths.borderEdges[columnIndex].hasStart &&
      !stickyColumns.includes(columnIndex)
    ) {
      startOffset += borderOffset;
    }
    for (let i = stickyColumns.length - 1; i > -1; i--) {
      const stickyIndex = stickyColumns[i];
      if (stickyIndex > columnIndex) {
        endOffset += appliedColumnWidths.realColumnWidths[stickyIndex];
      } else {
        break;
      }
    }
    // include the logical 'end' border of this column if owned by the next column if
    // this column is sticky - otherwise that border should already be visible
    if (
      !appliedColumnWidths.borderEdges[columnIndex].hasEnd &&
      !stickyColumns.includes(columnIndex)
    ) {
      endOffset += borderOffset;
    }

    const horizontalOverflowDiff = getHorizontalOverflowDiff(
      columnElement,
      getScrollerStart(scrollerElement, viewportInfo, sizingInfo, overflowInfo, startOffset, isRtl),
      getScrollerEnd(scrollerElement, viewportInfo, sizingInfo, overflowInfo, endOffset, isRtl),
      isRtl
    );
    const leftOverflowDiff = horizontalOverflowDiff.left;
    const hasLeftOverflow = leftOverflowDiff > 0;
    const rightOverflowDiff = horizontalOverflowDiff.right;
    const hasRightOverflow = rightOverflowDiff > 0;

    // don't adjust scroll position if row has overflow in both directions
    if (hasLeftOverflow && hasRightOverflow) {
      return;
    }

    if (location == null || location === 'inView') {
      // if column fits fully in viewport, scroll overflow side into view
      // otherwise, scroll the shortest amount to fill viewport with column
      if (hasLeftOverflow) {
        if (Math.abs(rightOverflowDiff) > Math.abs(leftOverflowDiff)) {
          scrollerElement.scrollLeft = _roundScrollValue(
            scrollerElement.scrollLeft - leftOverflowDiff,
            false
          );
        } else {
          scrollerElement.scrollLeft = _roundScrollValue(
            scrollerElement.scrollLeft + rightOverflowDiff,
            true
          );
        }
      } else if (hasRightOverflow) {
        if (Math.abs(leftOverflowDiff) > Math.abs(rightOverflowDiff)) {
          scrollerElement.scrollLeft = _roundScrollValue(
            scrollerElement.scrollLeft + rightOverflowDiff,
            true
          );
        } else {
          scrollerElement.scrollLeft = _roundScrollValue(
            scrollerElement.scrollLeft - leftOverflowDiff,
            false
          );
        }
      }
    } else if (location === 'start') {
      scrollerElement.scrollLeft = _roundScrollValue(
        scrollerElement.scrollLeft - leftOverflowDiff,
        false
      );
    } else {
      scrollerElement.scrollLeft = _roundScrollValue(
        scrollerElement.scrollLeft + rightOverflowDiff,
        true
      );
    }
  }
}

/**
 * A helper function to make sure specified elem is visible in the specified container
 */
const _scrollRowIntoView = (
  rootElement: HTMLDivElement,
  scrollerElement: HTMLElement,
  viewportInfo: ViewportInfo,
  sizingInfo: SizingInfo,
  tableId: string,
  rowElement: HTMLElement,
  location?: 'inView' | 'top' | 'bottom'
) => {
  const verticalOverflowDiff = getVerticalOverflowDiff(
    rowElement,
    getScrollerTop(rootElement, scrollerElement, viewportInfo.viewportConfig, tableId),
    getScrollerBottom(
      rootElement,
      scrollerElement,
      viewportInfo.viewportConfig,
      sizingInfo,
      tableId
    )
  );
  const topOverflowDiff = verticalOverflowDiff.top;
  const hasTopOverflow = topOverflowDiff > 0;
  const bottomOverflowDiff = verticalOverflowDiff.bottom;
  const hasBottomOverflow = bottomOverflowDiff > 0;

  // don't adjust scroll position if row has overflow in both directions
  if (hasTopOverflow && hasBottomOverflow) {
    return;
  }
  if (location == null || location === 'inView') {
    // if row fits fully in viewport, scroll overflow side into view
    // otherwise, scroll the shortest amount to fill viewport with row
    if (hasBottomOverflow) {
      if (Math.abs(topOverflowDiff) > Math.abs(bottomOverflowDiff)) {
        scrollerElement.scrollTop = _roundScrollValue(
          scrollerElement.scrollTop + bottomOverflowDiff,
          true
        );
      } else {
        scrollerElement.scrollTop = _roundScrollValue(
          scrollerElement.scrollTop - topOverflowDiff,
          false
        );
      }
    } else if (hasTopOverflow) {
      if (Math.abs(bottomOverflowDiff) > Math.abs(topOverflowDiff)) {
        scrollerElement.scrollTop = _roundScrollValue(
          scrollerElement.scrollTop - topOverflowDiff,
          false
        );
      } else {
        scrollerElement.scrollTop = _roundScrollValue(
          scrollerElement.scrollTop + bottomOverflowDiff,
          true
        );
      }
    }
  } else if (location === 'top') {
    scrollerElement.scrollTop = _roundScrollValue(
      scrollerElement.scrollTop - topOverflowDiff,
      false
    );
  } else {
    scrollerElement.scrollTop = _roundScrollValue(
      scrollerElement.scrollTop + bottomOverflowDiff,
      true
    );
  }
};

/**
 * Helper method to round a desired scrollTop or scrollLeft value to the nearest valid value
 * depending on the client browser.
 */
const _roundScrollValue = (value: number, isOver: boolean) => {
  // use Math.floor() and Math.ceil() to ensure we round to nearest containing valid
  // pixel value in either direction as valid scrollTop values are only integers
  return isOver ? Math.ceil(value) : Math.floor(value);
};

/**
 * Helper to set the horizontal scroll position on the Table.
 */
export function setHorizontalScrollPosition<K, D, C>(
  rootElement: HTMLDivElement,
  scrollerElement: HTMLElement,
  viewportInfo: ViewportInfo,
  sizingInfo: SizingInfo,
  overflowInfo: OverflowInfo,
  columnsArray: TableColumn<K, D, C>[],
  fullColumnWidths: number[],
  isRtl: boolean,
  horizontalScrollPosition?: TableHorizontalScrollPosition<C>
) {
  const x = _getScrollLeftFromScrollPosition(
    rootElement,
    scrollerElement,
    viewportInfo,
    sizingInfo,
    overflowInfo,
    columnsArray,
    fullColumnWidths,
    isRtl,
    horizontalScrollPosition
  );
  scrollerElement.scrollLeft = x ?? 0;
}

/**
 * Helper to set the vertical scroll position on the Table.
 */
export function setVerticalScrollPosition<K>(
  rootElement: HTMLDivElement,
  scrollerElement: HTMLElement,
  viewportInfo: ViewportInfo,
  tableId: string,
  verticalScrollPosition?: TableVerticalScrollPosition<K>
) {
  const y = _getScrollTopFromScrollPosition(
    rootElement,
    scrollerElement,
    viewportInfo,
    tableId,
    verticalScrollPosition
  );
  scrollerElement.scrollTop = y ?? 0;
}

/**
 * Helper to determine the 'x' value of a given scroll position.
 */
function _getScrollLeftFromScrollPosition<K, D, C>(
  rootElement: HTMLDivElement,
  scrollerElement: HTMLElement,
  viewportInfo: ViewportInfo,
  sizingInfo: SizingInfo,
  overflowInfo: OverflowInfo,
  columnsArray: TableColumn<K, D, C>[],
  fullColumnWidths: number[],
  isRtl: boolean,
  scrollPosition?: TableHorizontalScrollPosition<C>
) {
  if (scrollPosition != null) {
    let columnKey;
    let useColumn = false;
    if (scrollPosition.columnKey != null) {
      // columnKey takes precedence over everything else
      useColumn = true;
      columnKey = scrollPosition.columnKey;
    } else if (scrollPosition.offsetX != null && scrollPosition.x == null) {
      // offsetX can be used on its own if it is the only horizontal aspect provided
      useColumn = true;
      const currentHorizontalScrollPosition = getHorizontalScrollPosition(
        rootElement,
        scrollerElement,
        viewportInfo,
        sizingInfo,
        overflowInfo,
        columnsArray,
        fullColumnWidths,
        isRtl
      );
      columnKey = currentHorizontalScrollPosition.columnKey;
    }
    if (useColumn) {
      if (columnKey != null) {
        let offsetX;
        let columnEnd;
        const scrollerStart = getScrollerStart(
          scrollerElement,
          viewportInfo,
          sizingInfo,
          overflowInfo,
          0,
          isRtl
        );
        if (isRtl) {
          offsetX = -(scrollPosition.offsetX ?? 0);
          columnEnd = rootElement.getBoundingClientRect().right;
        } else {
          offsetX = scrollPosition.offsetX ?? 0;
          columnEnd = rootElement.getBoundingClientRect().left;
        }
        for (let i = 0; i < columnsArray.length; i++) {
          const column = columnsArray[i];
          if (column.key === columnKey) {
            return scrollerElement.scrollLeft - scrollerStart + columnEnd + offsetX;
          }
          if (column.value.sticky !== 'enabled') {
            columnEnd += isRtl ? -fullColumnWidths[i] : fullColumnWidths[i];
          }
        }
      }
    } else if (scrollPosition.x != null) {
      return scrollPosition.x;
    }
  }
  // either nothing was provided, or what was provided was invalid
  return undefined;
}

/**
 * Helper to determine the 'y' value of a given scroll position.
 */
function _getScrollTopFromScrollPosition<K>(
  rootElement: HTMLDivElement,
  scrollerElement: HTMLElement,
  viewportInfo: ViewportInfo,
  tableId: string,
  scrollPosition?: TableVerticalScrollPosition<K>
) {
  if (scrollPosition != null) {
    let rowKey;
    let useRow = false;
    if (scrollPosition.rowKey != null) {
      // rowKey takes precedence over everything else
      useRow = true;
      rowKey = scrollPosition.rowKey;
    } else if (scrollPosition.offsetY != null && scrollPosition.y == null) {
      // offsetY can be used on its own if it is the only vertical aspect provided
      useRow = true;
      rowKey = getVerticalScrollPosition(
        rootElement,
        scrollerElement,
        viewportInfo,
        tableId
      ).rowKey;
    }
    if (useRow) {
      if (rowKey != null) {
        const scrollerTop = getScrollerTop(
          rootElement,
          scrollerElement,
          viewportInfo.viewportConfig,
          tableId
        );
        const offsetY = scrollPosition.offsetY ?? 0;
        const rowElements = rootElement.querySelectorAll(`[${TABLE_ROW_SELECTOR}='${tableId}']`);
        for (let i = 0; i < rowElements.length; i++) {
          if (rowKey === getRowKey(rowElements[i] as HTMLElement)) {
            const rowTop = rowElements[i].getBoundingClientRect().top;
            return scrollerElement.scrollTop + rowTop - scrollerTop + offsetY;
          }
        }
      }
    } else if (scrollPosition.y != null) {
      return scrollPosition.y;
    }
  }
  // either nothing was provided, or what was provided was invalid
  return undefined;
}

/**
 * Helper to get the logical scroll position for the given scrollLeft value. If no scrollLeft value is given,
 * it will use the current scrollLeft value of the Table.
 */
export function getHorizontalScrollPosition<K, D, C>(
  rootElement: HTMLDivElement,
  scrollerElement: HTMLElement,
  viewportInfo: ViewportInfo,
  sizingInfo: SizingInfo,
  overflowInfo: OverflowInfo,
  columnsArray: TableColumn<K, D, C>[],
  fullColumnWidths: number[],
  isRtl: boolean
) {
  // ensure scroll position x value is initialized
  const scrollPosition: TableHorizontalScrollPosition<C> = {
    x: Math.abs(scrollerElement.scrollLeft),
    columnKey: undefined,
    offsetX: undefined
  };
  let value1;
  let value2;
  if (viewportInfo.isExternalScroller) {
    const scrollerStart = getScrollerStart(
      scrollerElement,
      viewportInfo,
      sizingInfo,
      overflowInfo,
      0,
      isRtl
    );
    if (isRtl) {
      value1 = rootElement.getBoundingClientRect().right;
      value2 = scrollerStart;
    } else {
      value1 = scrollerStart;
      value2 = rootElement.getBoundingClientRect().left;
    }
  } else if (!isRtl) {
    value1 = scrollerElement.scrollLeft;
    value2 = 0;
  } else {
    value1 = 0;
    value2 = scrollerElement.scrollLeft;
  }
  for (let i = 0; i < columnsArray.length; i++) {
    const columnWidth = fullColumnWidths[i];
    value1 -= columnWidth;
    const column = columnsArray[i];
    const isSticky = column.value.sticky === 'enabled';
    if (
      (!isSticky && value2 > value1) ||
      (isSticky && value2 >= value1 + columnWidth) ||
      i === columnsArray.length - 1
    ) {
      scrollPosition.columnKey = column.key;
      scrollPosition.offsetX = value1 - value2 + columnWidth;
      break;
    }
    if (isSticky) {
      value2 -= columnWidth;
    }
  }
  return scrollPosition;
}

/**
 * Helper to get the logical scroll position for the given scrollTop value. If no scrollTop value is given,
 * it will use the current scrollTop value of the Table.
 */
export function getVerticalScrollPosition<K>(
  rootElement: HTMLDivElement,
  scrollerElement: HTMLElement,
  viewportInfo: ViewportInfo,
  tableId: string
) {
  // ensure scroll position y value is initialized
  const scrollPosition: TableVerticalScrollPosition<K> = {
    y: Math.abs(scrollerElement.scrollTop),
    rowKey: undefined,
    offsetY: undefined
  };

  const scrollerTop = getScrollerTop(
    rootElement,
    scrollerElement,
    viewportInfo.viewportConfig,
    tableId
  );
  const rowElements = rootElement.querySelectorAll(`[${TABLE_ROW_SELECTOR}='${tableId}']`);
  for (let i = 0; i < rowElements.length; i++) {
    const rowRect = rowElements[i].getBoundingClientRect();
    if (rowRect.bottom > scrollerTop || i === rowElements.length - 1) {
      scrollPosition.rowKey = getRowKeyForRowIndex<K>(rootElement, tableId, i)!;
      scrollPosition.offsetY = scrollerTop - rowRect.top;
      break;
    }
  }
  return scrollPosition;
}

export function applyScrollDetail<K extends string | number, D, C extends string>(
  rootElement: HTMLDivElement,
  scrollerElement: HTMLElement,
  viewportInfo: ViewportInfo,
  tableId: string,
  columnsArray: TableColumn<K, D, C>[],
  stickyColumnInfo: StickyColumnInfo,
  sizingInfo: SizingInfo,
  overflowInfo: OverflowInfo,
  scrollDetail: ScrollDetail<K, C>,
  isRtl: boolean,
  appliedColumnWidths?: AppliedColumnWidths<C>
) {
  if (!sizingInfo.isInitialRender) {
    if (scrollDetail.cell.type === 'data') {
      const rowElement = findElementByKey(
        rootElement,
        scrollDetail.cell.rowKey,
        `[${TABLE_ROW_SELECTOR}='${tableId}']`
      ) as HTMLElement;
      if (rowElement != null && scrollDetail.locationY != null) {
        _scrollRowIntoView(
          rootElement,
          scrollerElement,
          viewportInfo,
          sizingInfo,
          tableId,
          rowElement,
          scrollDetail.locationY
        );
      }
    }
    if (
      scrollDetail.cell.type === 'data' ||
      scrollDetail.cell.type === 'header' ||
      scrollDetail.cell.type === 'footer'
    ) {
      const cellElement = getElementFromCell(rootElement, tableId, scrollDetail.cell);
      if (cellElement != null && scrollDetail.locationX != null) {
        _scrollColumnIntoView(
          cellElement,
          scrollerElement,
          viewportInfo,
          columnsArray,
          stickyColumnInfo,
          sizingInfo,
          overflowInfo,
          isRtl,
          appliedColumnWidths,
          scrollDetail.locationX
        );
      }
    } else if (scrollDetail.cell.type === 'loadMore' || scrollDetail.cell.type === 'loading') {
      const cellElement = getElementFromCell(rootElement, tableId, scrollDetail.cell);
      if (cellElement != null && scrollDetail.locationY != null) {
        _scrollRowIntoView(
          rootElement,
          scrollerElement,
          viewportInfo,
          sizingInfo,
          tableId,
          cellElement,
          scrollDetail.cell.type === 'loading' && scrollDetail.locationY === 'inView'
            ? 'top'
            : scrollDetail.locationY
        );
      }
    }
  }
}

/**
 * Helper function to update the 'left' and 'right' values of all sticky columns to correspond
 * to the given sizing information.
 */
export function updateStickyColumns<K, D, C extends string>(
  rootElement: HTMLDivElement,
  scrollerElement: HTMLElement,
  viewportInfo: ViewportInfo,
  tableId: string,
  isRtl: boolean,
  columnsArray: TableColumn<K, D, C>[],
  stickyColumnInfo: StickyColumnInfo,
  sizingInfo: SizingInfo,
  overflowInfo: OverflowInfo,
  fullColumnWidths: number[],
  stickyEdgesState: { start?: number; end?: number },
  setStickyEdgesState: (stickyEdgesState: { start?: number; end?: number }) => void,
  isHorizontalScroll: boolean
) {
  let stickyIndex;
  let stickyStartOffset = 0;
  let stickyEndOffset = 0;
  if (viewportInfo.isExternalScroller) {
    const scrollerOffsetStart = viewportInfo.viewportConfig.scrollerOffsetStart;
    if (scrollerOffsetStart != null) {
      stickyStartOffset = scrollerOffsetStart;
    }
    const scrollerOffsetEnd = viewportInfo.viewportConfig.scrollerOffsetEnd;
    if (scrollerOffsetEnd != null) {
      stickyEndOffset = scrollerOffsetEnd;
    }
  }

  const stickyIndicies = stickyColumnInfo.stickyColumns;
  for (let i = 0; i < stickyIndicies.length; i++) {
    stickyIndex = stickyIndicies[i];
    _applyStickyColumnOffset(rootElement, tableId, isRtl, stickyIndex, stickyStartOffset, true);
    stickyStartOffset += fullColumnWidths[stickyIndex];
  }
  for (let i = stickyIndicies.length - 1; i > -1; i--) {
    stickyIndex = stickyIndicies[i];
    _applyStickyColumnOffset(rootElement, tableId, isRtl, stickyIndex, stickyEndOffset, false);
    stickyEndOffset += fullColumnWidths[stickyIndex];
  }

  // only apply sticky edge styling if horizontal overflow is present
  if (isHorizontalScroll) {
    updateStickyEdges(
      rootElement,
      scrollerElement,
      viewportInfo,
      columnsArray,
      stickyColumnInfo,
      sizingInfo,
      overflowInfo,
      fullColumnWidths,
      isRtl,
      stickyEdgesState,
      setStickyEdgesState
    );
  } else if (stickyEdgesState.start != null || stickyEdgesState.end != null) {
    setStickyEdgesState({});
  }
}

/**
 * Helper function to get the sticky column indicies.
 */
export const getStickyColumnIndicies = <K, D, C>(columnsArray: TableColumn<K, D, C>[]) => {
  const stickyColumns = [];
  for (let i = 0; i < columnsArray.length; i++) {
    if (columnsArray[i].value.sticky === 'enabled') {
      stickyColumns.push(i);
    }
  }
  return stickyColumns;
};

/**
 * Helper function to update the 'left' and 'right' values of a specific column.
 */
const _applyStickyColumnOffset = (
  rootElement: HTMLDivElement,
  tableId: string,
  isRtl: boolean,
  columnIndex: number,
  stickyOffset: number,
  isStart: boolean
) => {
  const styleProperty = (isStart && !isRtl) || (!isStart && isRtl) ? 'left' : 'right';
  const styleValue = `${stickyOffset}px`;

  const headerElements = rootElement.querySelectorAll(`[${TABLE_HEADER_SELECTOR}='${tableId}']`);
  const headerElement = headerElements[columnIndex] as HTMLElement;
  headerElement.style[styleProperty] = styleValue;

  const rowElements = rootElement.querySelectorAll(`[${TABLE_ROW_SELECTOR}='${tableId}']`);
  for (let j = 0; j < rowElements.length; j++) {
    const rowElement = rowElements[j] as HTMLElement;
    const cellElements = rowElement.querySelectorAll(`[${TABLE_DATA_SELECTOR}='${tableId}']`);
    const cellElement = cellElements[columnIndex] as HTMLElement;
    cellElement.style[styleProperty] = styleValue;
  }

  const footerElements = rootElement.querySelectorAll(`[${TABLE_FOOTER_SELECTOR}='${tableId}']`);
  if (footerElements.length > 0) {
    const footerElement = footerElements[columnIndex] as HTMLElement;
    footerElement.style[styleProperty] = styleValue;
  }
};

/**
 * Helper function to update the sticky edges based on the given scrollLeft value.
 */
export function updateStickyEdges<K, D, C>(
  rootElement: HTMLDivElement,
  scrollerElement: HTMLElement,
  viewportInfo: ViewportInfo,
  columnsArray: TableColumn<K, D, C>[],
  stickyColumnInfo: StickyColumnInfo,
  sizingInfo: SizingInfo,
  overflowInfo: OverflowInfo,
  fullColumnWidths: number[],
  isRtl: boolean,
  stickyEdgesState: { start?: number; end?: number },
  setStickyEdgesState: (stickyEdgesState: { start?: number; end?: number }) => void
) {
  if (columnsArray.length > 0) {
    let i;
    let currentScrollIndex;
    const newScrollPosition = getHorizontalScrollPosition(
      rootElement,
      scrollerElement,
      viewportInfo,
      sizingInfo,
      overflowInfo,
      columnsArray,
      fullColumnWidths,
      isRtl
    );
    for (i = 0; i < columnsArray.length; i++) {
      if (columnsArray[i].key === newScrollPosition.columnKey) {
        currentScrollIndex = i;
        break;
      }
    }

    let startIndex;
    const stickyIndicies = stickyColumnInfo.stickyColumns;
    // browser zoom levels cause rounding issues where the start scroll
    // position may never be reached, but it should always be within 1
    if (newScrollPosition.x! >= 1) {
      for (i = 0; i < stickyIndicies.length; i++) {
        const currentIndex = stickyIndicies[i];
        if (currentIndex < currentScrollIndex!) {
          startIndex = currentIndex;
        } else {
          break;
        }
      }
    }

    let endIndex;
    let currentWidth = 0;
    const maxScrollPos = scrollerElement.scrollWidth - scrollerElement.clientWidth;
    const endOverflow = maxScrollPos - newScrollPosition.x!;
    // browser zoom levels cause rounding issues where the max scroll
    // position may never be reached, but it should always be within 1
    if (endOverflow >= 1) {
      for (let i = columnsArray.length - 1; i > -1; i--) {
        if (stickyIndicies.indexOf(i) !== -1) {
          endIndex = i;
        } else {
          currentWidth += fullColumnWidths[i];
          if (currentWidth > endOverflow) {
            break;
          }
        }
      }
    }
    if (stickyEdgesState.start != startIndex || stickyEdgesState.end != endIndex) {
      setStickyEdgesState({ start: startIndex, end: endIndex });
    }
  }
}

/**
 * Helper function to hide a currently shown tooltip once the anchor cell scrolls out of view.
 */
export function hideOutOfViewTooltip(
  rootElement: HTMLDivElement,
  scrollerElement: HTMLElement,
  viewportInfo: ViewportInfo,
  tableId: string,
  stickyColumnInfo: StickyColumnInfo,
  sizingInfo: SizingInfo,
  overflowInfo: OverflowInfo,
  fullColumnWidths: number[],
  isRtl: boolean,
  tooltipAnchorRef?: MutableRef<HTMLElement | undefined>,
  hideTooltip?: (isImmediate?: boolean) => void
) {
  if (tooltipAnchorRef?.current != null && hideTooltip != null) {
    const verticalOverflowDiff = getVerticalOverflowDiff(
      tooltipAnchorRef.current,
      getScrollerTop(rootElement, scrollerElement, viewportInfo.viewportConfig, tableId),
      getScrollerBottom(
        rootElement,
        scrollerElement,
        viewportInfo.viewportConfig,
        sizingInfo,
        tableId
      )
    );
    const topOverflowDiff = verticalOverflowDiff.top;
    const hasTopOverflow = topOverflowDiff >= 0;
    const bottomOverflowDiff = verticalOverflowDiff.bottom;
    const hasBottomOverflow = bottomOverflowDiff >= 0;

    if (
      (hasTopOverflow && Math.abs(topOverflowDiff) > tooltipAnchorRef.current.offsetHeight) ||
      (hasBottomOverflow && Math.abs(bottomOverflowDiff) > tooltipAnchorRef.current.offsetHeight)
    ) {
      hideTooltip(true);
      return;
    }

    let startOffset = 0;
    let endOffset = 0;

    const stickyColumns = stickyColumnInfo.stickyColumns;
    for (let i = 0; i < stickyColumns.length; i++) {
      const stickyIndex = stickyColumns[i];
      startOffset += fullColumnWidths[stickyIndex];
    }
    for (let i = stickyColumns.length - 1; i > -1; i--) {
      const stickyIndex = stickyColumns[i];
      endOffset += fullColumnWidths[stickyIndex];
    }
    const horizontalOverflowDiff = getHorizontalOverflowDiff(
      tooltipAnchorRef.current,
      getScrollerStart(scrollerElement, viewportInfo, sizingInfo, overflowInfo, startOffset, isRtl),
      getScrollerEnd(scrollerElement, viewportInfo, sizingInfo, overflowInfo, endOffset, isRtl),
      isRtl
    );
    const leftOverflowDiff = horizontalOverflowDiff.left;
    const hasLeftOverflow = leftOverflowDiff >= 0;
    const rightOverflowDiff = horizontalOverflowDiff.right;
    const hasRightOverflow = rightOverflowDiff >= 0;

    if (
      (hasLeftOverflow && Math.abs(leftOverflowDiff) > tooltipAnchorRef.current.offsetWidth) ||
      (hasRightOverflow && Math.abs(rightOverflowDiff) > tooltipAnchorRef.current.offsetWidth)
    ) {
      hideTooltip(true);
    }
  }
}

/**
 * Helper function to reset the scroll position of the scroller element
 */
export const resetVerticalScrollPosition = (scrollerElement: HTMLElement) => {
  scrollerElement.scrollTop = 0;
};

/**
 * Helper function to get the viewport config.
 */
export const getViewportConfig = (
  scrollerRef: RefObject<HTMLElement>,
  config?: TableViewportConfig
) => {
  return config ?? { scrollerRef };
};

/**
 * Helper function to update the scroller element's styling based on overflow status.
 */
export const applyOverflowStatus = (
  rootElement: HTMLDivElement,
  scrollerElement: HTMLElement,
  tableId: string,
  contentWidth: number,
  availableWidth: number,
  contentHeight: number,
  availableHeight: number,
  defaultScrollbarWidth: number,
  overflowInfoRef: MutableRef<OverflowInfo>,
  setHasVerticalUnderflow: Dispatch<boolean>
) => {
  // check for at least 0.5px difference to account for browser rounding issues
  const hasHorizontalOverflow = contentWidth - availableWidth > 0.5;
  const hasVerticalOverflow = contentHeight - availableHeight > 0.5;
  if (hasHorizontalOverflow) {
    scrollerElement.classList.remove(scrollerStyles.noHorizontalScroll);
    scrollerElement.classList.add(scrollerStyles.horizontalScroll);
  } else {
    scrollerElement.classList.remove(scrollerStyles.horizontalScroll);
    scrollerElement.classList.add(scrollerStyles.noHorizontalScroll);
  }
  if (hasVerticalOverflow) {
    scrollerElement.classList.remove(scrollerStyles.noVerticalScroll);
    scrollerElement.classList.add(scrollerStyles.verticalScroll);
    scrollerElement.style.paddingInlineEnd = '';
  } else {
    scrollerElement.classList.remove(scrollerStyles.verticalScroll);
    scrollerElement.classList.add(scrollerStyles.noVerticalScroll);
    scrollerElement.style.paddingInlineEnd = `${defaultScrollbarWidth}px`;
  }
  overflowInfoRef.current.isHorizontal = hasHorizontalOverflow;
  overflowInfoRef.current.isVertical = hasVerticalOverflow;
  applyUnderflowStatus(rootElement, tableId, setHasVerticalUnderflow);
};

/**
 * Helper function to update the undeflow status of the Table based on the spacer element sizing.
 */
export const applyUnderflowStatus = (
  rootElement: HTMLDivElement,
  tableId: string,
  setHasVerticalUnderflow: Dispatch<boolean>
) => {
  const spacerRowElement = getSpacerRowElement(rootElement, tableId);
  if (spacerRowElement != null) {
    setHasVerticalUnderflow(spacerRowElement.offsetHeight > 0);
  }
};
